import React, {Ref, useContext, useEffect, useRef, useState} from 'react';
import {Alert, Button, Col, DatePicker, Form, Input, Modal, Row, Select, Space} from 'antd';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {FormattedMessage, useIntl} from "react-intl";
// @ts-ignore
import Tags from '@yaireo/tagify/dist/react.tagify';
import {CaretDownOutlined, CloseOutlined, PlusOutlined} from "@ant-design/icons";
import ReactQuill from "react-quill";
import dayjs, {Dayjs} from "dayjs";
import {Procedure} from '../../api-client/';
import {
    MedicalStaff,
    Patient, StatusResponse,
    StatusToothCreate,
    Tooth,
    ToothStatus,
    ToothTreatment,
    Treatment,
    TreatmentToothCreate,
    TreatmentToothUpdate,
    ToothWithRelationships
} from "../../api-client";
import {getPatientApi, getMedicalStaffApi, getTreatmentsApi} from "../../api/apiInstances";
import {useForm} from "antd/es/form/Form";
import {ProcedureObject} from "./partials/ProcedureObject";
import ProcedureItem from "./partials/ProcedureItem";
import {AlertContext} from "../../context/AlertContext";
import {ALERT_DURATIONS} from "../../constants/alerts";
// @ts-ignore
import {v4 as uuidv4} from 'uuid';
import {isMobileOnly} from "react-device-detect";
import {calculateTotalPrice} from '../../utils/medicalUtils';
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import logger from "../../utils/logger";

interface PatientTreatmentPlanModalProps {
    mode: 'create' | 'edit';
    appointment: string;
    patient: Patient;
    title: string | undefined;
    toothObjs: ToothWithRelationships[];
    selectedTreatment: ToothTreatment;
    allTeethPositions: ToothWithRelationships[];
    isModalOpen: boolean;
    setIsModalOpen: Function;
    loadData: Function;
    sendDataToNZIS?: Function;
    statuses: StatusResponse[];
}

interface TagifyItem {
    value: string;
}

const PatientTreatmentPlanModal = ({
                                       appointment, statuses,
                                       patient, loadData, allTeethPositions, selectedTreatment,
                                       toothObjs, title, isModalOpen, setIsModalOpen, mode, sendDataToNZIS
                                   }: PatientTreatmentPlanModalProps) => {

    const intl = useIntl();
    const {locale, userOrganization} = useContext(DentobookApplicationContext);
    const {principal} = React.useContext(DentobookApplicationContext);
    const {setVisible, setSuccessMessage, setError} = useContext(AlertContext);
    const {Option} = Select;

    // Get organization currency for display (default to EUR if not set)
    const currency = userOrganization?.currency || 'EUR';
    const [loading, setLoading] = useState<boolean>(false);
    const [fieldsError, setFieldsError] = useState<string>('');
    const [positions, setPositions] = useState<number[]>([]);
    const [treatment, setTreatment] = useState<string>(selectedTreatment?.treatmentId || '');
    const [nzisTreatment, setNzisTreatment] = useState<string>(selectedTreatment?.nzisTreatmentId || '');
    const [diagnose, setDiagnose] = useState<string>(selectedTreatment?.statusId || '');
    const [nzisDiagnose, setNzisDiagnose] = useState<string>(selectedTreatment?.nzisStatusId || '');
    const [status, setStatus] = useState<string>(selectedTreatment?.status || '');
    const [date, setDate] = useState<Dayjs | undefined>(selectedTreatment?.date ? dayjs(selectedTreatment.date).utc() : dayjs());
    const [price, setPrice] = useState<number>(selectedTreatment?.price || 0);
    const [discount, setDiscount] = useState<number>(selectedTreatment?.discount || 0);
    const [laboratoryPrice, setLaboratoryPrice] = useState<number>(selectedTreatment?.laboratoryPrice || 0);
    const [doctor, setDoctor] = useState<string>(selectedTreatment?.medicalStaffId || '');
    const [paidAt, setPaidAt] = useState<string | undefined>(selectedTreatment?.paidAt || undefined);
    const [tab, setTab] = useState<number>(0);
    const [notes, setNotes] = useState<string>(selectedTreatment?.notes || '');
    const [doctors, setDoctors] = useState<MedicalStaff[]>([]);
    const [doctorsLoaded, setDoctorsLoaded] = useState(false);
    const [formProcedures, setFormProcedures] = useState<ProcedureObject[]>((selectedTreatment?.procedures || []) as ProcedureObject[]);
    const [procedures, setProcedures] = useState<Procedure[]>([]);
    const [treatments, setTreatments] = useState<Treatment[]>([]);
    const [initialDoctorSet, setInitialDoctorSet] = useState(false);
    const [conditionOptions, setConditionOptions] = useState<Array<any>>([]);
    const [diagnoseOptions, setDiagnoseOptions] = useState<Array<any>>([]);

    // A referenced master record is "archived" when the snapshot exists (i.e. it was
    // set at creation time) and the FK is no longer present in the active options list.
    const conditionIsArchived = mode === 'edit'
        && !!selectedTreatment?.treatmentNameSnapshot
        && !conditionOptions.some(o => o.value === selectedTreatment.treatmentId);

    const diagnoseIsArchived = mode === 'edit'
        && !!selectedTreatment?.statusNameSnapshot
        && !diagnoseOptions.some(o => o.value === selectedTreatment.statusId);

    const conditionOptionsWithArchived = conditionIsArchived && selectedTreatment?.treatmentId && selectedTreatment?.treatmentNameSnapshot
        ? [{value: selectedTreatment.treatmentId, label: selectedTreatment.treatmentNameSnapshot, disabled: true}, ...conditionOptions]
        : conditionOptions;

    const diagnoseOptionsWithArchived = diagnoseIsArchived && selectedTreatment?.statusId && selectedTreatment?.statusNameSnapshot
        ? [{value: selectedTreatment.statusId, label: selectedTreatment.statusNameSnapshot, disabled: true}, ...diagnoseOptions]
        : diagnoseOptions;

    // Doctor is considered archived when we are in edit mode, the ORIGINAL DB-assigned
    // doctor is still selected (user hasn't changed it), but that doctor is either:
    //   a) missing from the staff list entirely (soft-deleted / hard-removed), or
    //   b) present but inactive (deactivated).
    // Guard on doctorsLoaded to avoid flashing the "archived" state during the initial
    // empty-array transient before the fetch resolves.
    // We only check the ORIGINAL assignment (selectedTreatment.medicalStaffId) so that
    // explicitly picking an inactive doctor from the dropdown does NOT trigger the alert.
    const originalDoctorId = selectedTreatment?.medicalStaffId ?? null;
    const assignedDoctorInList = doctors.find(d => d.id === doctor);
    const doctorIsArchived = mode === 'edit'
        && !!doctor
        && doctorsLoaded
        && doctor === originalDoctorId
        && !assignedDoctorInList?.isActive;  // false for active, true for inactive or missing

    const doctorOptions = [
        ...(doctorIsArchived && doctor
            ? [{
                value: doctor,
                // Priority: snapshot written at creation time > live name from inactive
                // staff record > generic fallback (pre-migration records or no name).
                label: selectedTreatment?.medicalStaffNameSnapshot
                    ?? (assignedDoctorInList
                        ? `${assignedDoctorInList.firstName} ${assignedDoctorInList.lastName}`
                        : intl.formatMessage({id: 'archived_doctor'})),
                disabled: true,
              }]
            : []),
        ...doctors
            // When the assigned doctor is archived and still in the list (deactivated),
            // exclude them from the regular options to avoid showing them twice.
            .filter(d => !(doctorIsArchived && d.id === doctor) && (mode === 'edit' || d.isActive))
            .map(d => ({
                value: d.id!,
                label: d.firstName + ' ' + d.lastName + (!d.isActive ? ` (${intl.formatMessage({id: 'inactive'})})` : ''),
            }))
    ];

    const tagifyRef: Ref<any> = useRef();

    let [form] = useForm();

    const richTextEditorToolbarOptions = [
        [{'header': [1, 2, 3, 4, 5, 6, false]}],
        ['bold', 'italic', 'underline', 'strike']
    ];

    const patientApi = getPatientApi();
    const medicalApi = getMedicalStaffApi();
    const treatmentsApi = getTreatmentsApi();

    const toothObj = toothObjs.length > 0 ? toothObjs[0] : {};

    const initializeDiagnoses = () => {
        if (mode === 'create') {
            let diagnoseFromStatus = '';
            const toothStatusesLength = toothObjs.length ? toothObjs[0].toothStatuses!.length : 0;

            if (toothObjs.length && toothObjs[0].toothStatuses!.length) {
                diagnoseFromStatus = toothObjs[0].toothStatuses!
                    .sort((itemOne: ToothStatus, itemTwo: ToothStatus) => itemOne.createdAt?.localeCompare(itemTwo.createdAt!)!)[toothStatusesLength - 1].statusId!;
            }

            // Only pre-fill if the suggested status is still an active option.
            // If it has been archived (deleted), leave the field empty so the user
            // must choose a valid active option — never show a raw UUID in create mode.
            const activeValue = diagnoseOptions.some(o => o.value === diagnoseFromStatus) ? diagnoseFromStatus : '';
            setDiagnose(activeValue);
            form.setFieldsValue({diagnose: activeValue});
        }
    }

    const clearQuillInput = () => {
        setTimeout(() => {
            const quillElement = document.getElementsByClassName('ql-editor')[0];

            if (quillElement) {
                quillElement.innerHTML = '';
            }
            setNotes('');
        }, 0);
    };

    useEffect(() => {
        if (!toothObjs[0]) {
            setPositions([]);
        } else {
            setPositions(toothObjs.map(tooth => tooth?.position!));
        }
    }, [isModalOpen]);

    useEffect(() => {
        // Fetch including inactive so that a deactivated doctor assigned to a
        // historical record still resolves to a name in edit mode instead of
        // displaying a raw UUID.
        medicalApi.fetchAllMedicalStaff('', 'name', 1, 100, true)
            .then(res => {
                const allStaff = res.data.medicalStaff as MedicalStaff[];
                setDoctors(allStaff.filter(employee => employee.role === 'MEDICAL_DOCTOR' || employee.role === 'CLINIC_ADMIN'));
                setDoctorsLoaded(true);
                medicalApi.fetchAllProcedures()
                    .then(res => {
                        setProcedures(res.data);
                        treatmentsApi.fetchMedicalTreatments()
                            .then(res => {
                                setTreatments(res.data);
                                changeLang(res.data);
                            })
                            .catch(err => logger.log(err));
                    })
                    .catch(err => logger.log(err));
            })
            .catch(err => logger.log(err));
    }, []);

    // Reset form when the modal closes so stale UUIDs from the previous treatment
    // do not persist into the next open. The initialization effect below is guarded
    // with `if (!isModalOpen) return` and therefore never writes values on close.
    useEffect(() => {
        if (!isModalOpen) {
            form.resetFields();
        }
    }, [isModalOpen]);

    useEffect(() => {
        // Only run when the modal is actually open. Without this guard the effect
        // would fire on close (isModalOpen: true→false) and write the just-closed
        // treatment's UUIDs back into the form, causing them to appear as stale
        // values when the modal is next opened for a different treatment.
        if (!isModalOpen) return;

        calculateTotalPrice(selectedTreatment?.price || 0, selectedTreatment?.discount || 0, selectedTreatment?.laboratoryPrice || 0,
            (calculation: number) =>
                form.setFieldValue("totalPrice", calculation.toFixed(2)));

        setTab(0);
        setFieldsError('');

        if (!selectedTreatment?.id) {
            form.resetFields();
            // Reset all controlled component state so stale UUIDs from a previous
            // edit do not leak into a fresh create-mode open. form.resetFields()
            // only clears the Ant Design form store; Select/Input values driven by
            // component state need an explicit reset here.
            setTreatment('');
            setNzisTreatment('');
            setDiagnose('');
            setNzisDiagnose('');
            setStatus('');
            setPrice(0);
            setDiscount(0);
            setLaboratoryPrice(0);
            setDoctor('');
            setPaidAt(undefined);
            setInitialDoctorSet(false);
            setFormProcedures([]);
            initializeDiagnoses();
            clearQuillInput();

            return;
        }

        setPrice(selectedTreatment.price || 0);
        setDiagnose(selectedTreatment.statusId || '');
        setTreatment(selectedTreatment.treatmentId || '');
        setNzisTreatment(selectedTreatment.nzisTreatmentId || '');
        setNzisDiagnose(selectedTreatment.nzisStatusId || '');

        setFormProcedures(selectedTreatment.procedures || []);

        // Normalize status to UPPERCASE to match the Select Option values (IN_PROGRESS, DONE, etc.)
        const statusKey = selectedTreatment.status?.toUpperCase() || '';
        setStatus(statusKey);
        setDate(dayjs(selectedTreatment.date).utc() || undefined);
        setDoctor(selectedTreatment.medicalStaffId || '');
        setLaboratoryPrice(selectedTreatment.laboratoryPrice || 0);
        setDiscount(selectedTreatment.discount || 0);
        setPaidAt(selectedTreatment.paidAt || undefined);
        setNotes(selectedTreatment.notes || '');

        form.setFieldsValue({
            condition: selectedTreatment.treatmentId,
            nzisTreatment: selectedTreatment.nzisTreatmentId,
            diagnose: selectedTreatment.statusId,
            nzisDiagnose: selectedTreatment.nzisStatusId,
            status: statusKey,
            date: dayjs(selectedTreatment.date).utc(),
            doctor: selectedTreatment.medicalStaffId,
            price: selectedTreatment.price ?? 0,
            laboratoryPrice: selectedTreatment.laboratoryPrice ?? 0,
            discount: selectedTreatment.discount ?? 0,
            paidAt: selectedTreatment.paidAt ? '1' : '0',
            notes: selectedTreatment.notes
        });

        let i = 0;
        for (const formProcedure of formProcedures) {
            formProcedures[i].isPriceCalculatedInTreatment = formProcedure.price === 0;
            i++;
        }
    }, [isModalOpen, treatments.length, selectedTreatment?.id]);

    useEffect(() => {
        if (initialDoctorSet || selectedTreatment?.id || principal?.user?.role !== 'MEDICAL_DOCTOR') return;
        const myStaff = doctors.find(doc => doc.id === principal?.user?.medicalStaffId)
            ?? doctors.find(doc => doc.userId === principal?.user?.id);
        if (myStaff) {
            setDoctor(myStaff.id!);
            form.setFieldsValue({doctor: myStaff.id});
            setInitialDoctorSet(true);
        }
    }, [doctors, principal, selectedTreatment?.id, initialDoctorSet]);

    const handleDoctorChange = (value: string | undefined) => {
        setDoctor(value || "");
        form.setFieldsValue({doctor: value});
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    }

    const handleError = (err: any) => {
        logger.log(err);
        setLoading(false);
        setError(intl.formatMessage({id: 'unexpected_error'}));
        setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
        setIsModalOpen(false);
    };

    const handleOk = () => {
        let invalidToothFlag = false;
        const teethToPost = tagifyRef.current?.value.map((element: TagifyItem) => {
            const whitelist = allTeethPositions.map(toothPosition => toothPosition?.position!.toString());

            return allTeethPositions.filter(toothObject => {
                const toothPosition = +toothObject?.position!;
                const tagifyValue = +element.value;

                if (whitelist.indexOf(element.value) === -1) {
                    invalidToothFlag = true;
                }

                return toothPosition === tagifyValue;
            })[0];
        });

        if (invalidToothFlag) {
            setFieldsError(intl.formatMessage({id: 'invalid_tooth_position'}));

            return;
        }

        let valid = true;

        form.submit();

        form.validateFields()
            .then(res => {
                if (res.errorFields?.length) {
                    valid = false;
                } else {
                    if (!treatment || !status || !date || !doctor) {
                        valid = false;
                    }

                    let i = 0;
                    for (const formProcedure of formProcedures) {
                        if (!formProcedure.id || !formProcedure.date || !formProcedure.status) {
                            valid = false;
                        }

                        formProcedures[i].date = new Date(formProcedure.date!).toISOString();

                        if (formProcedure.isPriceCalculatedInTreatment) {
                            formProcedures[i].price = 0;
                            formProcedures[i].discount = 0;
                            formProcedures[i].laboratoryPrice = 0;
                        }
                        i++;
                    }

                    if (!valid) {
                        setFieldsError(intl.formatMessage({id: 'fill_all_required_fields'}));

                        return;
                    }

                    // Split logic by mode with proper DTO types
                    if (mode === 'edit') {
                        // Build update payload
                        const updatePayload: TreatmentToothUpdate[] = [];
                        const updateData: TreatmentToothUpdate = {
                            treatmentId: treatment,
                            medicalStaffId: doctor,
                            statusId: diagnose,
                            price,
                            discount,
                            laboratoryPrice,
                            status,
                            notes,
                            paidAt,
                            invoiced: false
                        };

                        for (const toothToPost of teethToPost) {
                            updatePayload.push(updateData);
                        }

                        if (teethToPost.length === 0) {
                            updatePayload.push(updateData);
                        }

                        // Build teethStatuses for update
                        const teethStatuses: StatusToothCreate[] = [];
                        teethToPost.forEach((toothToPost: ToothWithRelationships) => {
                            teethStatuses.push({
                                toothId: toothToPost.id,
                                statusId: diagnose,
                                nzisStatusId: nzisDiagnose || null
                            });
                        });

                        if (teethToPost.length === 0) {
                            teethStatuses.push({
                                toothId: null,
                                statusId: diagnose,
                                nzisStatusId: nzisDiagnose || null
                            });
                        }

                        // Call update APIs
                        setLoading(true);
                        patientApi.createToothStatus(appointment, teethStatuses)
                            .then(() => {
                                patientApi.updateToothTreatment(appointment, selectedTreatment.id!, updatePayload)
                                    .then(() => {
                                        setLoading(false);
                                        setSuccessMessage(intl.formatMessage({id: 'success_message'}));
                                        setVisible(true);
                                        setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                                        setIsModalOpen(false);
                                        loadData();

                                        if (sendDataToNZIS && updatePayload.length > 0) {
                                            const dataWithTooth = {
                                                ...updatePayload[0],
                                                tooth: toothObjs[0]
                                            };
                                            sendDataToNZIS(dataWithTooth);
                                        }
                                    })
                                    .catch(handleError);
                            })
                            .catch(handleError);
                    } else {
                        // Create mode
                        if (!patient?.id) {
                            logger.log("patient id is undefined.....");
                            return;
                        }

                        // Build create payload
                        const createPayload: TreatmentToothCreate[] = [];
                        const baseCreateData: Omit<TreatmentToothCreate, 'toothId'> = {
                            treatmentId: treatment,
                            nzisTreatmentId: nzisTreatment || null,
                            medicalStaffId: doctor,
                            statusId: diagnose,
                            nzisStatusId: nzisDiagnose || null,
                            price,
                            discount,
                            laboratoryPrice,
                            status,
                            notes,
                            isSentToNzis: sendDataToNZIS ? (selectedTreatment?.isSentToNzis ?? false) : false,
                            nrnDental: selectedTreatment?.nrnDental ?? '',
                            date: date?.utc().format('YYYY-MM-DD') + 'T09:19:28.426Z',
                            invoiced: false,
                            appointmentId: appointment
                        };

                        for (const toothToPost of teethToPost) {
                            createPayload.push({
                                ...baseCreateData,
                                toothId: toothToPost.id
                            });
                        }

                        if (teethToPost.length === 0) {
                            createPayload.push({
                                ...baseCreateData,
                                toothId: null
                            });
                        }

                        // Build teethStatuses for create
                        const teethStatuses: StatusToothCreate[] = [];
                        createPayload.forEach(item => {
                            teethStatuses.push({
                                toothId: item.toothId,
                                statusId: item.statusId,
                                nzisStatusId: item.nzisStatusId || null
                            });
                        });

                        // Call create APIs
                        setLoading(true);
                        patientApi.createToothStatus(appointment, teethStatuses)
                            .then(() => {
                                patientApi.createToothTreatment(appointment, createPayload)
                                    .then(() => {
                                        setLoading(false);
                                        setVisible(true);
                                        setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);

                                        loadData();

                                        setIsModalOpen(false);
                                    })
                                    .catch(handleError);
                            })
                            .catch(handleError);
                    }
                }
            })
            .catch(err => {
                const blankFieldExists = Object.values(err.values).filter(item => !item).length > 0;

                if (blankFieldExists) {
                    setError(intl.formatMessage({id: 'all_fields_required'}));
                    setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
                }
            });
    }

    const handleFilter = (input: string, option: any) => option.label.toLowerCase().includes(input.toLowerCase());

    const changeLang = (treatments: Treatment[]) => {
        setConditionOptions(treatments.sort((treatmentOne, treatmentTwo) => (treatmentOne.displayName ?? '').localeCompare(treatmentTwo.displayName ?? ''))
            .map(treatment => ({
                value: treatment.id,
                label: treatment.displayName ?? treatment.name ?? treatment.id,
            })));
        setDiagnoseOptions(statuses.sort((statusOne: StatusResponse, statusTwo: StatusResponse) => (statusOne.displayName ?? '').localeCompare(statusTwo.displayName ?? ''))
            .map(status => ({
                value: status.id,
                label: status.displayName ?? status.name ?? status.id,
            })));
    };

    useEffect(() => changeLang(treatments), [locale]);

    const modalCreateTitle = useIntl().formatMessage({id: 'add_plan_title'});

    const footer = <div className={"modal-btn-wrapper" + (sendDataToNZIS ? " modal-nzis-btn-wrapper" : "")}>
        <Button className="modal-btn" onClick={handleCancel}>
            {<FormattedMessage id="cancel"/>}
        </Button>
        <Button className="modal-btn" style={!sendDataToNZIS ? {} : {width: 'auto'}} type="primary" onClick={handleOk}
                disabled={loading}
        >
            {!loading ? <FormattedMessage id={!sendDataToNZIS ? "confirm" : "nzis_confirm"}/> :
                <FormattedMessage id="loading"/>}
        </Button>
    </div>

    const financesMobile = <>
        <Space size="middle">
            <Form.Item
                label={<FormattedMessage id="overall_price"/>}
                className={"dentobook-form-item dentobook-simple-input"}
                name="price"
                initialValue={0}
                rules={[
                    {
                        required: true,
                        message: <FormattedMessage id="required_field"/>
                    },
                    {
                        pattern: /^[0-9]+(\.[0-9]+)?$/,
                        message: <FormattedMessage id="digits_only_field"/>
                    }
                ]}
            >
                <Input
                    className="input-ninety-percents dentobook-inputs"
                    addonAfter={currency}
                    onChange={e => {
                        setPrice(+e.target.value);

                        calculateTotalPrice(+e.target.value, discount, laboratoryPrice,
                            (calculation: number) => form.setFieldValue('totalPrice', calculation.toFixed(2)));
                    }}/>
            </Form.Item>
            <Form.Item
                label={<FormattedMessage id="discount"/>}
                className={"dentobook-form-item dentobook-simple-input"}
                name="discount"
                initialValue={0}
                rules={[
                    {
                        required: true,
                        message: <FormattedMessage id="required_field"/>
                    },
                    {
                        pattern: /^[0-9]+(\.[0-9]+)?$/,
                        message: <FormattedMessage id="digits_only_field"/>
                    },
                    {
                        message: intl.formatMessage({id: 'discount_too_large'}),
                        validator: (_, value) => {
                            if (isNaN(value) || +value <= 100) {
                                return Promise.resolve();
                            } else {
                                return Promise.reject(new Error('Discount must not exceed 100%'));
                            }
                        }
                    }
                ]}
            >
                <Input
                    onChange={e => {
                        setDiscount(+e.target.value);

                        calculateTotalPrice(price, +e.target.value, laboratoryPrice,
                            (calculation: number) => form.setFieldValue('totalPrice', calculation.toFixed(2)));
                    }}/>
            </Form.Item>
        </Space>
        <Space size="middle">
            <Form.Item
                label={<FormattedMessage id="total"/>}
                className={"dentobook-form-item dentobook-simple-input"}
                name="totalPrice"
                initialValue={'0.00'}
            >
                <Input
                    disabled={true}
                    addonAfter={currency}
                    className="input-ninety-percents dentobook-inputs"/>
            </Form.Item>
        </Space>
    </>;

    const financesDesktop = <>
        <Row gutter={24}>
            <Col md={12}>
                <Form.Item
                    label={<FormattedMessage id="overall_price"/>}
                    className={"dentobook-form-item dentobook-simple-input"}
                    name="price"
                    initialValue={0}
                    rules={[
                        {
                            required: true,
                            message: <FormattedMessage id="required_field"/>
                        },
                        {
                            pattern: /^[0-9]+(\.[0-9]+)?$/,
                            message: <FormattedMessage id="digits_only_field"/>
                        }
                    ]}
                >
                    <Input
                        className="input-ninety-percents dentobook-inputs"
                        addonAfter={currency}
                        onChange={e => {
                            setPrice(+e.target.value);

                            calculateTotalPrice(+e.target.value, discount, laboratoryPrice,
                                (calculation: number) => form.setFieldValue('totalPrice', calculation.toFixed(2)));
                        }}/>
                </Form.Item>
            </Col>
            <Col md={12}>
                <Form.Item
                    label={<FormattedMessage id="discount"/>}
                    className={"dentobook-form-item dentobook-simple-input"}
                    name="discount"
                    initialValue={0}
                    rules={[
                        {
                            required: true,
                            message: <FormattedMessage id="required_field"/>
                        },
                        {
                            pattern: /^[0-9]+(\.[0-9]+)?$/,
                            message: <FormattedMessage id="digits_only_field"/>
                        },
                        {
                            message: intl.formatMessage({id: 'discount_too_large'}),
                            validator: (_, value) => {
                                if (isNaN(value) || +value <= 100) {
                                    return Promise.resolve();
                                } else {
                                    return Promise.reject('Discount must not exceed 100%');
                                }
                            }
                        }
                    ]}
                >
                    <Input
                        className="input-ninety-percents dentobook-inputs"
                        onChange={e => {
                            setDiscount(+e.target.value);

                            calculateTotalPrice(price, +e.target.value, laboratoryPrice,
                                (calculation: number) => form.setFieldValue('totalPrice', calculation.toFixed(2)));
                        }}/>
                </Form.Item>
            </Col>
        </Row>
        <Row gutter={24}>
            <Col md={12}>
                <Form.Item
                    label={<FormattedMessage id="total"/>}
                    className={"dentobook-form-item dentobook-simple-input"}
                    name="totalPrice"
                    initialValue={'0.00'}
                    rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                >
                    <Input
                        disabled={true}
                        addonAfter={currency}
                        className="input-ninety-percents dentobook-inputs"/>
                </Form.Item>
            </Col>
        </Row>
    </>

    return (
        <Modal
            transitionName=""
            className={'dentobook-modal'}
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={title ?? modalCreateTitle}
            width={800}
            styles={{body: {overflowY: 'auto', maxHeight: 'calc(100vh - 300px)'}}}
            confirmLoading={loading}
            footer={footer}
            onCancel={handleCancel}
            closeIcon={<CloseOutlined className={'icon-24px'}/>}
            open={isModalOpen}>
            <div style={{overflowY: 'auto', width: '100%', overflowX: 'hidden'}}>
                {fieldsError && <p className="text-red">{fieldsError}</p>}
                <Form.Item
                    labelCol={{span: 24}}
                    label={<FormattedMessage id="position"/>}>
                    <div className="borderless teeth-tagify mb-2">
                        <Tags
                            onChange={() => setPositions(tagifyRef.current?.value)}
                            value={positions} tagifyRef={tagifyRef}
                            whitelist={allTeethPositions.map(toothObj => toothObj?.position?.toString())}
                            enforceWhitelist={true}
                            disabled={mode === 'edit'}
                            settings={{
                                blacklist: [],
                                maxTags: 100,
                                dropdown: {
                                    enabled: 0
                                },
                                callbacks: {}
                            }}/>
                    </div>
                </Form.Item>
                <div style={{marginBottom: -25}}></div>
                {tab === 0 ?
                    <Form layout="vertical" form={form} scrollToFirstError={true}>
                        <Row gutter={24}>
                            <Col md={17}>
                                <Form.Item name="condition"
                                           className={"dentobook-form-item input-full-width"}
                                           label={<FormattedMessage id="treatment_label"/>}
                                           rules={[{required: !conditionIsArchived, message: <FormattedMessage id="required_field"/>}]}
                                >
                                    <Select
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={handleFilter}
                                        options={conditionOptionsWithArchived}
                                        placeholder={<FormattedMessage id="selectPlaceholder"/>}
                                        size={"large"}
                                        className="dentobook-input"
                                        suffixIcon={<CaretDownOutlined/>}
                                        value={treatment}
                                        onChange={e => {
                                            setTreatment(e);

                                            const treatmentObject = treatments.find(treatment => treatment.id === e);
                                            const treatmentPrice = typeof treatmentObject?.price === 'number' ? treatmentObject.price : Number(treatmentObject?.price) || 0;
                                            const treatmentLaboratoryPrice = typeof treatmentObject?.laboratoryPrice === 'number' ? treatmentObject.laboratoryPrice : Number(treatmentObject?.laboratoryPrice) || 0;

                                            setPrice(treatmentPrice);
                                            setLaboratoryPrice(treatmentLaboratoryPrice);

                                            form.setFieldValue('price', treatmentPrice);
                                            form.setFieldValue('laboratoryPrice', treatmentLaboratoryPrice);

                                            calculateTotalPrice(
                                                treatmentPrice,
                                                discount,
                                                treatmentLaboratoryPrice,
                                                (calculation: number) => form.setFieldValue('totalPrice', calculation.toFixed(2))
                                            );
                                        }}
                                        allowClear
                                    />
                                </Form.Item>
                                {conditionIsArchived && (
                                    <Alert
                                        type="warning"
                                        style={{marginTop: -16, marginBottom: 8}}
                                        message={<><FormattedMessage id="archived_treatment"/>: {selectedTreatment.treatmentNameSnapshot}</>}
                                    />
                                )}
                            </Col>
                            <Col md={7}>
                                <Form.Item name="status"
                                           className={"dentobook-form-item input-full-width"}
                                           label={<FormattedMessage id="status"/>}
                                           rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}>
                                    <Select
                                        placeholder={<FormattedMessage id="selectPlaceholder"/>}
                                        size={"large"}
                                        className="dentobook-input"
                                        suffixIcon={<CaretDownOutlined/>}
                                        value={status}
                                        onChange={e => setStatus(e)}
                                        allowClear
                                    >
                                        <Option value="IN_PROGRESS"><FormattedMessage id="in_progress"/></Option>
                                        <Option value="DONE"><FormattedMessage id="done"/></Option>
                                        <Option value="OFFERED"><FormattedMessage id="offered"/></Option>
                                        <Option value="CANCELED"><FormattedMessage id="canceled"/></Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="diagnose"
                                   className={"dentobook-form-item input-full-width"}
                                   label={<FormattedMessage id="condition"/>}
                                   rules={[{required: !diagnoseIsArchived, message: <FormattedMessage id="required_field"/>}]}
                        >
                            <Select
                                showSearch
                                optionFilterProp="label"
                                filterOption={handleFilter}
                                options={diagnoseOptionsWithArchived}
                                placeholder={<FormattedMessage id="selectPlaceholder"/>}
                                size={"large"}
                                value={diagnose}
                                onChange={e => setDiagnose(e)}
                                className="dentobook-input"
                                suffixIcon={<CaretDownOutlined/>}
                                allowClear
                            />
                        </Form.Item>
                        {diagnoseIsArchived && (
                            <Alert
                                type="warning"
                                style={{marginTop: -16, marginBottom: 8}}
                                message={<><FormattedMessage id="archived_condition"/>: {selectedTreatment.statusNameSnapshot}</>}
                            />
                        )}
                        <Row gutter={24}>
                            <Col md={13}>
                                <Form.Item
                                    className={"dentobook-form-item input-full-width mt-6"}
                                    initialValue={date || dayjs()}
                                    name="date"
                                    label={<FormattedMessage id="date"/>}
                                    rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}>
                                    <DatePicker
                                        size={"large"}
                                        bordered={true}
                                        name={"date"}
                                        style={{width: 340}}
                                        className="input-ninety-nine-percent"
                                        format={"DD.MM.YYYY"}
                                        onChange={e => {
                                            if (e === null) {
                                                setDate(undefined);
                                            } else {
                                                setDate(e.utc());
                                            }
                                        }} placeholder=""/>
                                </Form.Item>
                            </Col>
                            <Col md={11}>
                                <Form.Item name="doctor"
                                           className={"dentobook-form-item input-full-width mt-6"}
                                           label={<FormattedMessage id="dental_doctor"/>}
                                           rules={[{required: !doctorIsArchived, message: <FormattedMessage id="required_field"/>}]}>
                                    <Select
                                        placeholder={<FormattedMessage id="selectPlaceholder"/>}
                                        size={"large"}
                                        className="dentobook-input"
                                        suffixIcon={<CaretDownOutlined/>}
                                        options={doctorOptions}
                                        value={doctor || undefined}
                                        onChange={handleDoctorChange}
                                        allowClear
                                    />
                                </Form.Item>
                                {doctorIsArchived && (
                                    <Alert
                                        type="warning"
                                        style={{marginTop: -16, marginBottom: 8}}
                                        message={<FormattedMessage id="archived_doctor"/>}
                                    />
                                )}
                            </Col>
                        </Row>

                        {isMobileOnly ? financesMobile : financesDesktop}

                        <Form.Item
                            label={<FormattedMessage id="notes"/>}
                            name="notes"
                            initialValue={notes}
                            className="dentobook-form-item dentobook-simple-input "
                        >
                            <ReactQuill
                                value={notes}
                                onChange={e => setNotes(e)}
                                modules={{
                                    toolbar: richTextEditorToolbarOptions
                                }}/>
                        </Form.Item>
                    </Form>
                    : <Form layout="vertical">
                        {formProcedures.map((formProcedure) => (
                            <ProcedureItem
                                key={formProcedure.uid}
                                form={form}
                                procedure={formProcedure}
                                procedures={procedures}
                                onDelete={() => setFormProcedures(
                                    formProcedures.filter(procedure => procedure.uid !== formProcedure.uid))}/>
                        ))}
                        <Button shape="round"
                                className="dentobook-btn-medium color-primary"
                                type="default"
                                onClick={() => setFormProcedures(formProcedures.concat({
                                    uid: uuidv4(),
                                    isPriceCalculatedInTreatment: true,
                                    id: procedures[0].id,
                                    date: (new Date()).toISOString(),
                                    price: 0,
                                    laboratoryPrice: 0
                                }))}><PlusOutlined/> <FormattedMessage
                            id="add_procedure"/></Button>
                    </Form>}
            </div>
        </Modal>
    );
};

export default PatientTreatmentPlanModal;